import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Pagination,
  Button,
  Stack,
} from "@mui/material";
import {
  RefreshOutlined,
  EditOutlined,
  DeleteOutlineRounded,
  FilterAltOutlined,
  FilterAltOffOutlined,
  VisibilityOutlined,
  PictureAsPdfOutlined,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { adminService } from "../services/adminService";
import EduFilterDialog from "../components/EduFilterDialog";
import EduEditDialog from "../components/EduEditDialog";
import EduDescDialog from "../components/EduDescDialog";
import { textPrimary, textSecondary } from "../../../constants/consts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PAGE_SIZE = 13;

/* Tarih → satır rengi */
const todayYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

const dateYMD = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  } catch {
    return "";
  }
};

const rowBgOfDate = (iso) => {
  const d = dateYMD(iso);
  const t = todayYMD();
  if (!d) return "#fff";
  if (d === t) return "rgba(59,130,246,0.10)"; // bugün — mavi
  // Karşılaştırma için timestamp
  const dt = new Date(iso).getTime();
  const todayMidnight = new Date().setHours(0, 0, 0, 0);
  if (dt < todayMidnight) return "rgba(245,158,11,0.10)"; // geçmiş — turuncu
  return "rgba(34,197,94,0.10)"; // gelecek — yeşil
};

const trToAscii = (s = "") =>
  String(s)
    .replace(/ş/g, "s")
    .replace(/Ş/g, "S")
    .replace(/ğ/g, "g")
    .replace(/Ğ/g, "G")
    .replace(/ı/g, "i")
    .replace(/İ/g, "I")
    .replace(/ç/g, "c")
    .replace(/Ç/g, "C")
    .replace(/ö/g, "o")
    .replace(/Ö/g, "O")
    .replace(/ü/g, "u")
    .replace(/Ü/g, "U");

function formatDateTR(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
  } catch {
    return iso;
  }
}

export default function AdminEdu() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);

  const [sunucuKoduList, setSunucuKoduList] = useState([]);
  const [personelList, setPersonelList] = useState([]);

  const [filters, setFilters] = useState({ sunucuKodu: "", eduDate: "" });
  const [filterOpen, setFilterOpen] = useState(false);

  const [editDialog, setEditDialog] = useState({ open: false, edu: null });
  const [descDialog, setDescDialog] = useState({
    open: false,
    title: "",
    desc: "",
  });

  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "info",
  });

  /* ─────────── Veri yükleme ─────────── */
  const loadData = async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const [edus, companies, supporters] = await Promise.all([
        adminService.getEdus(),
        adminService.getCompanies(),
        adminService.getUsersByType({ type: "destek", sunucuKodu: "" }),
      ]);

      const mapped = edus.map((e) => ({
        id: String(e.id),
        title: e.title ?? "-",
        sunucuKodu: e.sunucuKodu ?? e.sunucu_kodu ?? "",
        supporter: e.supporter ?? "",
        eduDate: e.eduDate ?? e.edu_date ?? null,
        duration: e.duration ?? "",
        desc: e.desc ?? "",
      }));

      setRows(mapped);
      setPage(1);
      setSunucuKoduList(companies.map((c) => c.sunucu_kodu).filter(Boolean));
      const names = supporters.map((u) => u.username).filter(Boolean);
      setPersonelList(names);
    } catch (e) {
      console.error(e);
      setError("Eğitimler yüklenemedi.");
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, []);

  /* ─────────── Filtreleme ─────────── */
  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filters.sunucuKodu && r.sunucuKodu !== filters.sunucuKodu)
        return false;
      if (filters.eduDate) {
        const d1 = dateYMD(r.eduDate);
        const d2 = filters.eduDate; // yyyy-MM-dd
        // dateYMD "y-m-d" döndürüyor (ay 0'dan başlıyor), filtre formatı 1'den
        // normalize: y-m-d → y-M-d
        const [y, m, d] = d2.split("-").map(Number);
        const normalized = `${y}-${m - 1}-${d}`;
        if (d1 !== normalized) return false;
      }
      return true;
    });
  }, [rows, filters]);

  const hasActiveFilter =
    (filters.sunucuKodu && filters.sunucuKodu !== "") ||
    (filters.eduDate && filters.eduDate !== "");

  /* ─────────── Sayfalama ─────────── */
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  /* ─────────── Silme ─────────── */
  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.title}" eğitimi silinsin mi?`)) return;
    try {
      await adminService.deleteEdu(row.id);
      setSnack({ open: true, msg: "Eğitim silindi.", severity: "success" });
      await loadData(true);
    } catch (e) {
      console.error(e);
      setSnack({
        open: true,
        msg: "Silme başarısız.",
        severity: "error",
      });
    }
  };

  /* ─────────── PDF ─────────── */
  const handlePdf = () => {
    try {
      const sorted = [...filtered].sort(
        (a, b) => new Date(a.eduDate) - new Date(b.eduDate),
      );

      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(16);
      doc.text(trToAscii("Egitim Kayit Listesi"), 14, 16);
      doc.setFontSize(9);
      doc.text(
        trToAscii(`Tarih: ${new Date().toLocaleString("tr-TR")}`),
        14,
        22,
      );

      const body = sorted.map((r) => [
        trToAscii(r.title),
        trToAscii(r.sunucuKodu),
        trToAscii(r.supporter || "-"),
        trToAscii(formatDateTR(r.eduDate)),
        trToAscii(String(r.duration || "-")),
      ]);

      autoTable(doc, {
        startY: 28,
        head: [["Konu", "Sunucu Kodu", "Personel", "Tarih", "Sure"]],
        body,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [4, 87, 129] },
      });

      const blobUrl = doc.output("bloburl");
      const newTab = window.open(blobUrl, "_blank");
      if (!newTab) {
        doc.save(`egitimler-${Date.now()}.pdf`);
        setSnack({
          open: true,
          msg: "Popup engellendi, PDF indirildi.",
          severity: "info",
        });
      }
    } catch (e) {
      console.error(e);
      setSnack({
        open: true,
        msg: "PDF oluşturulamadı.",
        severity: "error",
      });
    }
  };

  /* ─────────── Render ─────────── */
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        gap: 1.5,
      }}
    >
      {/* Başlık */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          flexWrap: "wrap",
          animation: `${fadeInUp} 0.4s ease-out both`,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={800}
          color="#fff"
          sx={{ fontSize: 18 }}
        >
          Eğitim Listesi
        </Typography>
        <Box
          sx={{
            px: 1.25,
            py: 0.4,
            borderRadius: 5,
            backgroundColor: "rgba(255,255,255,0.14)",
            color: "#fff",
            fontWeight: 700,
            fontSize: 12,
            border: "1px solid rgba(255,255,255,0.18)",
          }}
        >
          {filtered.length} kayıt
        </Box>

        <Stack direction="row" spacing={0.75} sx={{ ml: "auto" }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<FilterAltOutlined />}
            onClick={() => setFilterOpen(true)}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: "rgba(255,255,255,0.14)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.20)",
              fontWeight: 700,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.24)" },
            }}
          >
            Filtre
          </Button>
          {hasActiveFilter && (
            <Tooltip title="Filtreyi temizle">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setFilters({ sunucuKodu: "", eduDate: "" })}
                  sx={{
                    color: "#fff",
                    backgroundColor: "rgba(255,255,255,0.14)",
                    border: "1px solid rgba(255,255,255,0.20)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.24)" },
                  }}
                >
                  <FilterAltOffOutlined sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Button
            size="small"
            variant="contained"
            startIcon={<PictureAsPdfOutlined />}
            onClick={handlePdf}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: "rgba(255,255,255,0.14)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.20)",
              fontWeight: 700,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.24)" },
            }}
          >
            PDF
          </Button>
          <Tooltip title="Yenile">
            <span>
              <IconButton
                size="small"
                onClick={() => loadData(true)}
                disabled={loading || refreshing}
                sx={{
                  color: "#fff",
                  backgroundColor: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
                }}
              >
                <RefreshOutlined
                  sx={{
                    fontSize: 18,
                    animation:
                      loading || refreshing
                        ? "spin 1.2s linear infinite"
                        : "none",
                    "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* Aktif filtre etiketleri */}
      {hasActiveFilter && (
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ flexWrap: "wrap", gap: 0.75 }}
        >
          {filters.sunucuKodu && (
            <FilterChip label={`Sunucu: ${filters.sunucuKodu}`} />
          )}
          {filters.eduDate && (
            <FilterChip label={`Tarih: ${filters.eduDate}`} />
          )}
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#fff" }} />
        </Box>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              borderRadius: 2.5,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "rgba(255,255,255,0.98)",
              boxShadow: "0 12px 32px rgba(0,0,0,0.20)",
              border: "1px solid rgba(255,255,255,0.30)",
              animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s backwards`,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: 6, height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(15,50,65,0.20)",
                  borderRadius: 3,
                },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        background:
                          "linear-gradient(180deg, #F1F5F9 0%, #E2E8F0 100%)",
                        color: "#334155",
                        fontWeight: 800,
                        fontSize: 10.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        py: 1,
                        px: 1.5,
                        borderBottom: "1px solid #CBD5E1",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell>Konu</TableCell>
                    <TableCell>Sunucu</TableCell>
                    <TableCell>Personel</TableCell>
                    <TableCell>Eğitim Tarihi</TableCell>
                    <TableCell>Süre</TableCell>
                    <TableCell>Açıklama</TableCell>
                    <TableCell align="right">İşlem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: textSecondary,
                          fontSize: 13,
                        }}
                      >
                        Kayıt bulunamadı
                      </TableCell>
                    </TableRow>
                  ) : (
                    pageRows.map((r) => (
                      <TableRow
                        key={r.id}
                        sx={{
                          backgroundColor: rowBgOfDate(r.eduDate),
                          transition: "background-color 0.2s ease",
                          "& td": {
                            py: 0.75,
                            px: 1.5,
                            fontSize: 12.5,
                            color: "#334155",
                            borderBottom: "1px solid #F1F5F9",
                          },
                          "&:hover": {
                            filter: "brightness(0.97)",
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: textPrimary,
                            maxWidth: 240,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={r.title}
                        >
                          {r.title}
                        </TableCell>
                        <TableCell>{r.sunucuKodu}</TableCell>
                        <TableCell>{r.supporter || "-"}</TableCell>
                        <TableCell
                          sx={{ whiteSpace: "nowrap", fontWeight: 700 }}
                        >
                          {formatDateTR(r.eduDate)}
                        </TableCell>
                        <TableCell>{r.duration || "-"}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Açıklamayı gör">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setDescDialog({
                                  open: true,
                                  title: r.title,
                                  desc: r.desc,
                                })
                              }
                              sx={{
                                color: "#15803D",
                                "&:hover": {
                                  backgroundColor: "rgba(21,128,61,0.10)",
                                },
                              }}
                            >
                              <VisibilityOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title="Düzenle">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setEditDialog({ open: true, edu: r })
                              }
                              sx={{
                                color: "#045781",
                                "&:hover": {
                                  backgroundColor: "rgba(4,87,129,0.10)",
                                },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(r)}
                              sx={{
                                color: "#B91C1C",
                                "&:hover": {
                                  backgroundColor: "rgba(185,28,28,0.10)",
                                },
                              }}
                            >
                              <DeleteOutlineRounded sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          {filtered.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
                px: 0.5,
                animation: `${fadeInUp} 0.4s ease-out 0.2s backwards`,
              }}
            >
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  fontSize: 11.5,
                  fontWeight: 600,
                }}
              >
                {Math.min((safePage - 1) * PAGE_SIZE + 1, filtered.length)}–
                {Math.min(safePage * PAGE_SIZE, filtered.length)} /{" "}
                {filtered.length} kayıt
              </Typography>
              <Pagination
                count={totalPages}
                page={safePage}
                onChange={(_, p) => setPage(p)}
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#fff",
                    "&.Mui-selected": {
                      backgroundColor: "rgba(255,255,255,0.24)",
                      fontWeight: 800,
                    },
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.14)" },
                  },
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Dialoglar */}
      <EduFilterDialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={(f) => setFilters(f)}
        sunucuKoduList={sunucuKoduList}
      />

      <EduDescDialog
        open={descDialog.open}
        onClose={() => setDescDialog({ open: false, title: "", desc: "" })}
        title={descDialog.title}
        desc={descDialog.desc}
      />

      <EduEditDialog
        open={editDialog.open}
        edu={editDialog.edu}
        sunucuKoduList={sunucuKoduList}
        personelList={personelList}
        onClose={() => setEditDialog({ open: false, edu: null })}
        onSaved={() => {
          setSnack({
            open: true,
            msg: "Eğitim güncellendi.",
            severity: "success",
          });
          loadData(true);
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: 2 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ─────────── Yardımcı bileşenler ─────────── */
function FilterChip({ label }) {
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.4,
        borderRadius: 5,
        backgroundColor: "rgba(255,255,255,0.14)",
        color: "#fff",
        fontWeight: 700,
        fontSize: 11.5,
        border: "1px solid rgba(255,255,255,0.20)",
      }}
    >
      {label}
    </Box>
  );
}
